import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";

import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import MarkunreadOutlinedIcon from "@material-ui/icons/MarkunreadOutlined";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PersonIcon from "@material-ui/icons/Person";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import StoreIcon from "@material-ui/icons/Store";
import ReceiptIcon from "@material-ui/icons/Receipt";
import MapIcon from "@material-ui/icons/Map";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import OrderDetails from "shopAdminViews/orders/OrderDetails";
import TripDetails from "superAdminViews/ride/trips/TripDetails";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import TableToolbar from "designV2/components/TableToolbar";
import Button from "designV2/components/Button";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import Reply from "./Reply";
import "./superTickets.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * One component now serves all three tabs. The three files it replaces
 * (Unreplied/Replied/ClosedConversations, ~1,150 lines each) were the
 * same screen copied three times, differing only in the two booleans
 * below — so every fix had to be made three times, and in practice was
 * not: the three had already drifted apart.
 * ------------------------------------------------------------------ */

const CONVERSATION_FILTERS = `
  conversationCategory: $conversationCategory
  conversationType: $conversationType
  repliedByAdmin: $repliedByAdmin
  closed: $closed
  title: $title
  passengerName: $passengerName
  passengerPhoneNumber: $passengerPhoneNumber
  driverName: $driverName
  driverPhoneNumber: $driverPhoneNumber
  shopName: $shopName
  shopPhoneNumber: $shopPhoneNumber
  createdAtFrom: $createdAtFrom
  createdAt: $createdAt
`;

const CONVERSATION_VARIABLES = `
  $limit: Int
  $skip: Int
  $conversationCategory: ConversationCategory
  $conversationType: ConversationType
  $repliedByAdmin: Boolean
  $closed: Boolean
  $title: String
  $passengerName: String
  $passengerPhoneNumber: String
  $driverName: String
  $driverPhoneNumber: String
  $shopName: String
  $shopPhoneNumber: String
  $createdAtFrom: Date
  $createdAt: Date
  $createdAtSort: Int
`;

const GET_CONVERSATIONS = gql`
  query getConversations(${CONVERSATION_VARIABLES}) {
    getConversations(
      Pagination: { limit: $limit, skip: $skip }
      filters: { ${CONVERSATION_FILTERS} }
      sort: { createdAt: $createdAtSort }
    ) {
      _id
      title
      closed
      conversationCategory
      conversationType
      adminUnreadCount
      createdAt
      user {
        _id
        fullName
        phoneNumber
      }
      driver {
        _id
        fullName
        phoneNumber
      }
      shop {
        _id
        name
        phoneNumbers
      }
      lastMessage {
        text
      }
      messages {
        _id
        text
        senderType
        messageType
        createdAt
        user {
          _id
          fullName
        }
        driver {
          _id
          fullName
        }
      }
      order {
        _id
        status
        rejectedFor
        shipmentModel
        finalPrice
        createdAt
        user {
          _id
          fullName
          phoneNumber
        }
        shop {
          _id
          name
        }
        tracking {
          trackId
        }
        cart {
          products {
            quantity
            product {
              _id
              title
            }
          }
        }
      }
      trip {
        _id
        ended
        cost
        tripType
        tripDistance
        state
        createdAt
        rate
        staticMapImageUrl
        waitTimePrice
        optionsPrice
        optionsPriceDetails {
          option
          price
        }
        tipValue
        driver {
          _id
          fullName
          phoneNumber
        }
        passengerDetail {
          _id
          fullName
          phoneNumber
        }
      }
    }
    getConversationsCount(filters: { ${CONVERSATION_FILTERS} })
  }
`;

// Queue sizes across every ticket rather than the page on screen, so the
// tab you are on still tells you what is waiting on the others.
const GET_QUEUE_COUNTS = gql`
  query getTicketQueueCounts {
    unreplied: getConversationsCount(filters: { repliedByAdmin: false, closed: false })
    replied: getConversationsCount(filters: { repliedByAdmin: true, closed: false })
    closed: getConversationsCount(filters: { closed: true })
  }
`;

const CLOSE_CONVERSATION = gql`
  mutation closeConversation($id: ID!) {
    closeConversation(id: $id) {
      _id
      closed
    }
  }
`;

const MARK_READ = gql`
  mutation makeUnreadMessagesZero($id: ID!) {
    makeUnreadMessagesZero(id: $id) {
      _id
      adminUnreadCount
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants
 * ------------------------------------------------------------------ */

const STATUS_CONFIG = {
  unreplied: {
    repliedByAdmin: false,
    closed: false,
    title: "Unreplied tickets",
    description: "Customers, drivers and shops still waiting on a first reply from Spark.",
    emptyTitle: "Nothing waiting on you",
    emptyMessage: "Every open conversation has had at least one reply.",
    readOnly: false,
    closable: true,
  },
  replied: {
    repliedByAdmin: true,
    closed: false,
    title: "Replied tickets",
    description: "Open conversations that have been answered but not resolved yet.",
    emptyTitle: "No open replied tickets",
    emptyMessage: "Answered conversations that are still open will show up here.",
    readOnly: false,
    closable: true,
  },
  closed: {
    closed: true,
    title: "Closed tickets",
    description: "Resolved conversations, kept for the record.",
    emptyTitle: "No closed tickets yet",
    emptyMessage: "Conversations move here once they are closed.",
    readOnly: true,
    closable: false,
  },
};

const CATEGORY_OPTIONS = [
  { value: "SUPPORT_TICKET", label: "Support ticket" },
  { value: "MESSAGE", label: "Message" },
];
const CATEGORY_LABELS = CATEGORY_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

const TYPE_OPTIONS = [
  { value: "RIDE", label: "Ride" },
  { value: "DELIVERY", label: "Delivery" },
  { value: "FOOD", label: "Food" },
  { value: "GROCERY", label: "Grocery" },
  { value: "SUPPORT", label: "Support" },
  { value: "SHOP_SUPPORT", label: "Shop support" },
];
const TYPE_LABELS = TYPE_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});

// Same idea as the finance screens: the old tabs each had six ANDed
// name/phone inputs, and filling in two of them always returned nothing.
const SEARCH_TARGETS = [
  { value: "title", label: "Title" },
  { value: "passenger", label: "Customer" },
  { value: "driver", label: "Driver" },
  { value: "shop", label: "Shop" },
];
const SEARCH_LABELS = SEARCH_TARGETS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const SEARCH_PLACEHOLDERS = {
  title: "Search by ticket title...",
  passenger: "Search by customer name or phone number...",
  driver: "Search by driver name or phone number...",
  shop: "Search by shop name or phone number...",
};

const SORT_OPTIONS = [
  { value: "oldest", label: "Oldest first" },
  { value: "newest", label: "Newest first" },
];

const FILTER_DEBOUNCE_MS = 350;

const looksLikePhone = (value) => /^[\d\s+()-]+$/.test(value);
const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const readableError = (error) => {
  if (!error) return "Something went wrong.";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) return error.graphQLErrors[0].message;
  return error.message || "Something went wrong.";
};

// A conversation belongs to whichever of the three raised it.
const partyOf = (conversation) => {
  if (conversation.shop) {
    return {
      role: "Shop",
      name: conversation.shop.name,
      detail: (conversation.shop.phoneNumbers || [])[0],
      icon: <StoreIcon fontSize="small" />,
    };
  }
  if (conversation.driver && !conversation.user) {
    return {
      role: "Driver",
      name: conversation.driver.fullName,
      detail: conversation.driver.phoneNumber,
      icon: <LocalShippingIcon fontSize="small" />,
    };
  }
  if (conversation.user) {
    return {
      role: "Customer",
      name: conversation.user.fullName,
      detail: conversation.user.phoneNumber,
      icon: <PersonIcon fontSize="small" />,
    };
  }
  return null;
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const ConversationsList = ({ status }) => {
  const config = STATUS_CONFIG[status];

  const [searchTarget, setSearchTarget] = useState("title");
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState(status === "unreplied" ? "oldest" : "newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [selectedIds, setSelectedIds] = useState([]);
  const [openConversation, setOpenConversation] = useState(null);
  const [orderTarget, setOrderTarget] = useState(null);
  const [tripTarget, setTripTarget] = useState(null);
  const [closeTarget, setCloseTarget] = useState(null);
  const [closing, setClosing] = useState(false);
  const [actionError, setActionError] = useState("");
  const [actionResult, setActionResult] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: status === "unreplied" ? 1 : -1,
    repliedByAdmin: config.repliedByAdmin,
    closed: config.closed,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      const term = searchTerm.trim();
      const search = {};
      if (term) {
        if (searchTarget === "title") search.title = term;
        else if (looksLikePhone(term)) search[`${searchTarget}PhoneNumber`] = term;
        else search[`${searchTarget}Name`] = term;
      }

      setVariables((v) => ({
        ...v,
        skip: 0,
        title: undefined,
        passengerName: undefined,
        passengerPhoneNumber: undefined,
        driverName: undefined,
        driverPhoneNumber: undefined,
        shopName: undefined,
        shopPhoneNumber: undefined,
        ...search,
        conversationCategory: categoryFilter || undefined,
        conversationType: typeFilter || undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : -1,
      }));
      setPageCount(0);
      setSelectedIds([]);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [searchTarget, searchTerm, categoryFilter, typeFilter, dateFrom, dateTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
    setSelectedIds([]);
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_CONVERSATIONS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: queueData, refetch: refetchQueue } = useQuery(GET_QUEUE_COUNTS, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });

  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_CONVERSATIONS, {
    fetchPolicy: "network-only",
  });

  const [closeConversation] = useMutation(CLOSE_CONVERSATION);
  const [makeUnreadMessagesZero] = useMutation(MARK_READ);

  const conversations = (data && data.getConversations) || [];
  const totalCount = (data && data.getConversationsCount) || 0;
  const queue = queueData || {};
  const unreadOnPage = conversations.reduce((sum, c) => sum + (c.adminUnreadCount || 0), 0);

  /* ---------------- opening a ticket ---------------- */

  // adminUnreadCount was fetched and shown on every one of the old tabs
  // but never cleared, so the badge on a ticket — and the unread total in
  // the sidebar — only ever went up. Opening a ticket now marks it read.
  const openTicket = (conversation) => {
    setOpenConversation(conversation);
    if (!conversation.adminUnreadCount) return;
    makeUnreadMessagesZero({ variables: { id: conversation._id } })
      .then(() => {
        refetch();
        refetchQueue();
      })
      .catch(() => {
        /* a failed read receipt should not interrupt reading the ticket */
      });
  };

  /* ---------------- selection + closing ---------------- */

  const toggleSelectOne = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.concat(id)
    );

  const toggleSelectAll = () => {
    const ids = conversations.map((c) => c._id);
    setSelectedIds((current) => (current.length === ids.length ? [] : ids));
  };

  const checkboxState =
    conversations.length > 0 && selectedIds.length === conversations.length
      ? "checked"
      : selectedIds.length > 0
      ? "indeterminate"
      : "unchecked";

  // closeConversation takes one id, so a batch is a sequence of calls;
  // one failure is reported without abandoning the rest.
  const runClose = () => {
    const targets = closeTarget || [];
    setClosing(true);
    setActionError("");
    setActionResult("");

    let succeeded = 0;
    const failures = [];

    const next = (index) => {
      if (index >= targets.length) {
        setClosing(false);
        setCloseTarget(null);
        setSelectedIds([]);
        setOpenConversation(null);
        refetch();
        refetchQueue();
        if (failures.length) {
          setActionError(`Closed ${succeeded} of ${targets.length}. ${failures.join(" ")}`);
        } else {
          setActionResult(`Closed ${succeeded} ticket${succeeded === 1 ? "" : "s"}.`);
        }
        return;
      }
      const conversation = targets[index];
      closeConversation({ variables: { id: conversation._id } })
        .then(() => {
          succeeded += 1;
          next(index + 1);
        })
        .catch((err) => {
          failures.push(`${conversation.title}: ${readableError(err)}`);
          next(index + 1);
        });
    };

    next(0);
  };

  /* ---------------- filter chips ---------------- */

  const trimmedTerm = searchTerm.trim();
  const activeFilterChips = [];
  if (trimmedTerm) {
    activeFilterChips.push({ id: "search", label: `${SEARCH_LABELS[searchTarget]}: ${trimmedTerm}` });
  }
  if (categoryFilter) {
    activeFilterChips.push({ id: "category", label: `Category: ${CATEGORY_LABELS[categoryFilter]}` });
  }
  if (typeFilter) activeFilterChips.push({ id: "type", label: `Type: ${TYPE_LABELS[typeFilter]}` });
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "category") setCategoryFilter("");
    if (id === "type") setTypeFilter("");
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setCategoryFilter("");
    setTypeFilter("");
    setDateFrom("");
    setDateTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [
      ["Title", "From", "Role", "Contact", "Category", "Type", "Unread", "Messages", "Created"],
    ];
    exportData.getConversations.forEach((c) => {
      const party = partyOf(c);
      rows.push([
        c.title,
        (party && party.name) || "-",
        (party && party.role) || "-",
        (party && party.detail) || "-",
        CATEGORY_LABELS[c.conversationCategory] || c.conversationCategory,
        TYPE_LABELS[c.conversationType] || c.conversationType,
        c.adminUnreadCount || 0,
        (c.messages && c.messages.length) || 0,
        moment(c.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Tickets");
    XLSX.writeFile(wb, `${config.title.replace(/\s+/g, "-")}.xlsx`);
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page superTicketsPage">
        <main className="main">
          <PageHeader title={config.title} description={config.description} />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  const selectedConversations = conversations.filter((c) => selectedIds.includes(c._id));
  const openParty = openConversation ? partyOf(openConversation) : null;

  return (
    <div className="page superTicketsPage">
      <main className="main">
        <PageHeader
          title={config.title}
          count={totalCount}
          description={config.description}
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} ticket${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<ChatBubbleOutlineIcon />} title="In this tab" value={totalCount} />
          <MetricCard
            icon={<HourglassEmptyIcon />}
            title="Awaiting first reply"
            value={queue.unreplied || 0}
            tone={queue.unreplied > 0 ? "warning" : "default"}
            hint="Across all tickets"
          />
          <MetricCard
            icon={<MarkunreadOutlinedIcon />}
            title="Unread messages"
            value={unreadOnPage}
            tone={unreadOnPage > 0 ? "danger" : "default"}
            hint="On this page"
          />
          <MetricCard icon={<DoneAllIcon />} title="Closed" value={queue.closed || 0} />
        </section>

        <section className="filters">
          <SearchField
            placeholder={SEARCH_PLACEHOLDERS[searchTarget]}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={`Search in: ${SEARCH_LABELS[searchTarget]}`}
              options={SEARCH_TARGETS}
              value={searchTarget}
              active={false}
              onChange={(e) => setSearchTarget(e.target.value)}
            />
            <FilterDropdown
              label={categoryFilter ? `Category: ${CATEGORY_LABELS[categoryFilter]}` : "Category"}
              options={[{ value: "", label: "All categories" }, ...CATEGORY_OPTIONS]}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />
            <FilterDropdown
              label={typeFilter ? `Type: ${TYPE_LABELS[typeFilter]}` : "Type"}
              options={[{ value: "", label: "All types" }, ...TYPE_OPTIONS]}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList
            filters={activeFilterChips}
            onClearFilter={clearFilter}
            onClearAll={clearAllFilters}
          />
        )}

        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            config.closable ? (
              <Button
                variant="secondary"
                size="sm"
                icon={<DoneAllIcon fontSize="small" />}
                onClick={() => setCloseTarget(selectedConversations)}
              >
                Close selected
              </Button>
            ) : null
          }
          rightActions={
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => {
                refetch();
                refetchQueue();
              }}
              title="Refresh tickets"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}
        {actionResult && (
          <div className="dsFormMessage success" role="status">
            {actionResult}
          </div>
        )}

        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell tableHeaderCheckbox">
                <Checkbox
                  size="small"
                  checked={checkboxState === "checked"}
                  indeterminate={checkboxState === "indeterminate"}
                  onChange={toggleSelectAll}
                  inputProps={{ "aria-label": "Select every ticket on this page" }}
                />
              </div>
              <div className="tableHeaderCell">Ticket</div>
              <div className="tableHeaderCell">From</div>
              <div className="tableHeaderCell">Category / type</div>
              <div className="tableHeaderCell">Created</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {conversations.length === 0 ? (
              <EmptyState
                icon={<ChatBubbleOutlineIcon />}
                title={activeFilterChips.length === 0 ? config.emptyTitle : "No matches"}
                message={
                  activeFilterChips.length === 0
                    ? config.emptyMessage
                    : "No tickets match these filters. Try widening or clearing them."
                }
              />
            ) : (
              conversations.map((conversation) => {
                const party = partyOf(conversation);
                const unread = conversation.adminUnreadCount || 0;

                return (
                  <div
                    className={`row superTicketRow ${unread > 0 ? "isUnread" : ""}`}
                    key={conversation._id}
                    onClick={() => openTicket(conversation)}
                  >
                    <span onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(conversation._id)}
                        onChange={() => toggleSelectOne(conversation._id)}
                        inputProps={{ "aria-label": `Select ${conversation.title}` }}
                      />
                    </span>

                    <div className="superTicketCell" data-label="Ticket">
                      <h4>
                        <span className="superTicketTitleText">{conversation.title}</span>
                        {unread > 0 && <StatusBadge status="danger" label={`${unread} new`} dot={false} />}
                      </h4>
                      <p>{(conversation.lastMessage && conversation.lastMessage.text) || "No messages yet"}</p>
                    </div>

                    <div className="superTicketFromCell" data-label="From">
                      {party ? party.icon : <PersonIcon fontSize="small" />}
                      <div className="superTicketFromText">
                        <h4>{(party && party.name) || "Unknown"}</h4>
                        <p>{(party && party.detail) || (party && party.role) || "-"}</p>
                      </div>
                    </div>

                    <span data-label="Category / type">
                      <span className="superTicketTag">
                        {CATEGORY_LABELS[conversation.conversationCategory] ||
                          conversation.conversationCategory}
                      </span>
                      <span className="superTicketTag muted">
                        {TYPE_LABELS[conversation.conversationType] || conversation.conversationType}
                      </span>
                    </span>

                    <span className="updated" data-label="Created">
                      <strong>{formatDate(conversation.createdAt)}</strong>
                      <span>{formatTime(conversation.createdAt)}</span>
                    </span>

                    <div className="actions" onClick={(e) => e.stopPropagation()}>
                      {conversation.order && (
                        <IconButton
                          size="small"
                          className="rowActionButton"
                          onClick={() => setOrderTarget(conversation.order)}
                          title="View linked order"
                        >
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      )}
                      {conversation.trip && (
                        <IconButton
                          size="small"
                          className="rowActionButton"
                          onClick={() => setTripTarget(conversation.trip)}
                          title="View linked trip"
                        >
                          <MapIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={() => openTicket(conversation)}
                        title={config.readOnly ? "View" : "Reply"}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      {/* TICKET */}
      <DialogShell
        open={Boolean(openConversation)}
        onClose={() => setOpenConversation(null)}
        title={openConversation ? openConversation.title : ""}
        subtitle={
          openConversation
            ? `${(openParty && openParty.name) || "Unknown"}${
                openParty ? ` · ${openParty.role}` : ""
              } · ${formatDate(openConversation.createdAt)}`
            : ""
        }
        size="lg"
      >
        {openConversation && (
          <React.Fragment>
            <div className="superTicketLinks">
              {openConversation.order && (
                <button
                  type="button"
                  className="superTicketLink"
                  onClick={() => setOrderTarget(openConversation.order)}
                >
                  <ReceiptIcon fontSize="small" /> Linked order
                </button>
              )}
              {openConversation.trip && (
                <button
                  type="button"
                  className="superTicketLink"
                  onClick={() => setTripTarget(openConversation.trip)}
                >
                  <MapIcon fontSize="small" /> Linked trip
                </button>
              )}
              {config.closable && (
                <button
                  type="button"
                  className="superTicketLink isDanger"
                  onClick={() => setCloseTarget([openConversation])}
                >
                  <CheckCircleOutlineIcon fontSize="small" /> Close ticket
                </button>
              )}
            </div>
            <Reply
              conversation={openConversation}
              readOnly={config.readOnly}
              onSent={() => {
                refetch();
                refetchQueue();
              }}
            />
          </React.Fragment>
        )}
      </DialogShell>

      {/* CLOSE CONFIRMATION */}
      <DialogShell
        open={Boolean(closeTarget)}
        onClose={() => (closing ? null : setCloseTarget(null))}
        title={closeTarget && closeTarget.length > 1 ? "Close tickets" : "Close ticket"}
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setCloseTarget(null), disabled: closing },
          {
            label: closing ? "Closing" : "Close",
            onClick: runClose,
            variant: "primary",
            loading: closing,
            disabled: !closeTarget || closeTarget.length === 0,
          },
        ]}
      >
        <div className="superTicketConfirm">
          {closeTarget && closeTarget.length > 1
            ? `${closeTarget.length} tickets will be marked resolved and moved to the closed tab. Nobody can reply to them afterwards.`
            : `This ticket will be marked resolved and moved to the closed tab. Nobody can reply to it afterwards.`}
        </div>
      </DialogShell>

      {/* LINKED ORDER */}
      <DialogShell
        open={Boolean(orderTarget)}
        onClose={() => setOrderTarget(null)}
        title="Order details"
        subtitle={orderTarget ? `#${orderTarget._id.slice(-8).toUpperCase()}` : ""}
      >
        {orderTarget && <OrderDetails order={orderTarget} />}
      </DialogShell>

      {/* LINKED TRIP */}
      <DialogShell
        open={Boolean(tripTarget)}
        onClose={() => setTripTarget(null)}
        title="Trip details"
        subtitle={tripTarget ? `#${tripTarget._id.slice(-8).toUpperCase()}` : ""}
        size="lg"
      >
        {tripTarget && <TripDetails trip={tripTarget} />}
      </DialogShell>
    </div>
  );
};

export default ConversationsList;
